import axios from 'axios';
import { supabase } from '../lib/supabase';
import { Project, Task, CreateProjectDto, CreateTaskDto } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let inMemoryAccessToken: string | null = null;

export function setApiTokens(tokens: { accessToken?: string | null }) {
  inMemoryAccessToken = tokens.accessToken ? String(tokens.accessToken) : null;
}

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = inMemoryAccessToken || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ---- Silent session recovery ----
// The user must never see a raw 401/403. On either, we try once to re-mint the app JWT
// from a still-valid Supabase session and retry the original request. If there's no
// session to recover from, we send them to a clean login instead of surfacing the error.
let recovering: Promise<boolean> | null = null;

async function recoverAppToken(): Promise<boolean> {
  if (!recovering) {
    recovering = (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return false;
        // /api/auth/* is exempt from the handler below, so this can't recurse.
        const res = await apiClient.post('/api/auth/supabase-exchange', { accessToken: session.access_token });
        const newToken = res.data?.accessToken;
        if (!newToken) return false;
        inMemoryAccessToken = String(newToken);
        localStorage.setItem('token', String(newToken));
        return true;
      } catch {
        return false;
      }
    })();
    // Let the next failure wave start a fresh attempt.
    recovering.finally(() => { recovering = null; });
  }
  return recovering;
}

let redirectingToLogin = false;
function redirectToLogin() {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  localStorage.removeItem('token');
  inMemoryAccessToken = null;
  supabase.auth.signOut().catch(() => {}).finally(() => {
    window.location.href = '/';
  });
}

// Never surface a 401/403 — recover the session, retry once, or go to a clean login.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const cfg = error.config || {};
    const isAuthEndpoint = (cfg.url || '').includes('/api/auth/');

    if ((status === 401 || status === 403) && !isAuthEndpoint) {
      if (!(cfg as any)._authRetried) {
        (cfg as any)._authRetried = true;
        const recovered = await recoverAppToken();
        if (recovered) {
          // Retry once with the freshly minted token (request interceptor re-adds it).
          return apiClient(cfg);
        }
        // No Supabase session to recover from → clean login, error never surfaces.
        redirectToLogin();
        return new Promise(() => {}); // hang; the page is navigating away
      }
      // Already retried with a fresh token and still 401 → the session is truly dead.
      if (status === 401) {
        redirectToLogin();
        return new Promise(() => {});
      }
      // A 403 that survives a fresh token is a genuine permission denial, not a dead
      // session — don't log the user out (that would loop); let the caller handle it.
    }
    return Promise.reject(error);
  },
);

// Projects API
export const projectsApi = {
  getAll: () => apiClient.get<Project[]>('/projects'),
  getOne: (id: string) => apiClient.get<Project>(`/projects/${id}`),
  create: (data: CreateProjectDto) => apiClient.post<Project>('/projects', data),
  update: (id: string, data: Partial<CreateProjectDto>) =>
    apiClient.put<Project>(`/projects/${id}`, data),
  delete: (id: string) => apiClient.delete(`/projects/${id}`),
};

// Beacon — Business Capability Model (per-tenant)
export const beaconApi = {
  getModel: () => apiClient.get<import('../types/beacon').BeaconModel>('/beacon'),
  // Domains
  createDomain: (data: any) => apiClient.post('/beacon/domains', data),
  updateDomain: (id: string, data: any) => apiClient.put(`/beacon/domains/${id}`, data),
  deleteDomain: (id: string) => apiClient.delete(`/beacon/domains/${id}`),
  // Capabilities
  createCapability: (data: any) => apiClient.post('/beacon/capabilities', data),
  updateCapability: (id: string, data: any) => apiClient.put(`/beacon/capabilities/${id}`, data),
  deleteCapability: (id: string) => apiClient.delete(`/beacon/capabilities/${id}`),
  // Sub-capabilities
  createSubCapability: (data: any) => apiClient.post('/beacon/sub-capabilities', data),
  updateSubCapability: (id: string, data: any) => apiClient.put(`/beacon/sub-capabilities/${id}`, data),
  deleteSubCapability: (id: string) => apiClient.delete(`/beacon/sub-capabilities/${id}`),
  // Applications
  createApplication: (data: any) => apiClient.post('/beacon/applications', data),
  updateApplication: (id: string, data: any) => apiClient.put(`/beacon/applications/${id}`, data),
  deleteApplication: (id: string) => apiClient.delete(`/beacon/applications/${id}`),
  // Initiatives
  createInitiative: (data: any) => apiClient.post('/beacon/initiatives', data),
  updateInitiative: (id: string, data: any) => apiClient.put(`/beacon/initiatives/${id}`, data),
  deleteInitiative: (id: string) => apiClient.delete(`/beacon/initiatives/${id}`),
};

// Tenant Roles
export interface TenantRole {
  id: string;
  tenantId: string;
  name: string;
  permissions?: {
    pages?: Record<string, boolean>;
    canEdit?: Record<string, boolean>;
  } | null;
  isDefault?: boolean;
  createdAt: string;
}

// Users API (DB users)
export interface DbUser {
  id: string;
  email: string;
  username?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  avatarUrl?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  role?: string | null;
  status?: string | null;
  adGroups?: string[];
  cognitoGroups?: string[];
  capacity?: number | null;
  costRate?: number | null;
  permissions?: { pages?: Record<string, string> } | null;
  isOrgAdmin?: boolean;
  hasReports?: boolean;
  managerId?: string | null;
  tenantRoleId?: string | null;
  tenantRole?: TenantRole | null;
  createdAt: string;
  updatedAt: string;
}

export interface OrgChartUser {
  id: string;
  email: string;
  displayName: string | null;
  username: string | null;
  managerId: string | null;
  isOrgAdmin: boolean;
  permissions: { pages?: Record<string, string> } | null;
  tenantRoleId: string | null;
  tenantRole: { id: string; name: string } | null;
}

export const usersApi = {
  getAll: (params?: { role?: string; status?: string; department?: string; scopePage?: string }) =>
    apiClient.get<DbUser[]>('/users', { params }),
  me: () => apiClient.get<DbUser>('/users/me'),
  updateMe: (data: Partial<Pick<DbUser, 'username' | 'displayName' | 'firstName' | 'lastName'>>) =>
    apiClient.put<DbUser>('/users/me', data),
  ensure: (email?: string) => apiClient.post<DbUser>('/users/ensure', { email }),
  getOrgChart: (params?: { scope?: 'managed' | 'all' }) =>
    apiClient.get<OrgChartUser[]>('/users/org-chart', { params }),
  setManager: (userId: string, managerId: string | null) =>
    apiClient.put<DbUser>(`/users/${userId}/manager`, { managerId }),
  updatePermissions: (userId: string, pages: Record<string, string>) =>
    apiClient.put<DbUser>(`/users/${userId}/permissions`, { pages }),
  setMyManager: (managerId: string) =>
    apiClient.post<DbUser>('/users/me/set-manager', { managerId }),
  getRoles: () => apiClient.get<TenantRole[]>('/users/roles'),
  createRole: (name: string) => apiClient.post<TenantRole>('/users/roles', { name }),
  updateRolePermissions: (roleId: string, perms: { pages: Record<string, boolean>; canEdit: Record<string, boolean> }) =>
    apiClient.put<TenantRole>(`/users/roles/${roleId}/permissions`, perms),
  deleteRole: (roleId: string) => apiClient.delete(`/users/roles/${roleId}`),
  setMyRole: (tenantRoleId: string | null) =>
    apiClient.put<DbUser>('/users/me/role', { tenantRoleId }),
  setUserRole: (userId: string, tenantRoleId: string | null) =>
    apiClient.put<DbUser>(`/users/${userId}/role`, { tenantRoleId }),
};

export const authApi = {
  login: (email: string, password: string) =>
    apiClient.post<{ accessToken: string; user: any }>('/api/auth/login', { email, password }),
  register: (data: { email: string; password: string; firstName?: string; lastName?: string; tenantCode?: string; tenantName?: string }) =>
    apiClient.post<{ accessToken: string; user: any }>('/api/auth/register', data),
  google: (credential: string, tenantCode?: string) =>
    apiClient.post<{ accessToken: string | null; user: any; isNewUser?: boolean; needsTenant?: boolean; googleEmail?: string; googleId?: string }>('/api/auth/google', { credential, tenantCode }),
  me: () => apiClient.get<{ success: boolean; user: any }>('/api/auth/me'),
  getTenant: () => apiClient.get<{ success: boolean; slug: string; name: string; code?: string }>('/api/auth/tenant'),
  lookupTenant: (code: string) =>
    apiClient.get<{ success: boolean; name?: string; error?: string }>('/api/auth/tenant/lookup', { params: { code } }),
  setupTenant: (name: string, companySize?: string) =>
    apiClient.post<{ success: boolean; tenant: { id: string; name: string; slug: string }; debug?: any }>('/api/auth/setup-tenant', { name, companySize }),
  myTenants: () =>
    apiClient.get<{ success: boolean; currentTenantId: string; tenants: Array<{ id: string; name: string; slug: string; userId: string }> }>('/api/auth/my-tenants'),
  switchTenant: (tenantId: string) =>
    apiClient.post<{ success: boolean; accessToken: string; user: any }>('/api/auth/switch-tenant', { tenantId }),
  supabaseExchange: (accessToken: string, tenantId?: string) =>
    apiClient.post<{
      accessToken: string | null;
      user: any;
      isNewUser?: boolean;
      needsTenant?: boolean;
      needsTenantPicker?: boolean;
      googleEmail?: string;
      googleId?: string;
      tenants?: Array<{ id: string; name: string; slug: string; userId: string }>;
    }>('/api/auth/supabase-exchange', { accessToken, tenantId }),
};

// Tasks API
export const tasksApi = {
  getAll: (projectId?: string) =>
    apiClient.get<Task[]>('/tasks', { params: { projectId } }),
  getOne: (id: string) => apiClient.get<Task>(`/tasks/${id}`),
  getByProject: (projectId: string) =>
    apiClient.get<Task[]>(`/tasks/project/${projectId}`),
  create: (data: CreateTaskDto) => apiClient.post<Task>('/tasks', data),
  update: (id: string, data: Partial<CreateTaskDto>) =>
    apiClient.put<Task>(`/tasks/${id}`, data),
  delete: (id: string) => apiClient.delete(`/tasks/${id}`),
};

// Jira API
export interface JiraProject {
  id: string;
  key: string;
  name: string;
  projectTypeKey: string;
  avatarUrls?: {
    '48x48'?: string;
  };
}

export interface JiraProjectsResponse {
  success: boolean;
  count: number;
  projects: JiraProject[];
  sandbox?: boolean;
  error?: string;
  hint?: string;
}

export interface JiraIssue {
  id: string;
  key: string;
  fields: {
    summary: string;
    status: {
      name: string;
    };
    assignee?: {
      displayName: string;
      avatarUrls?: {
        '16x16'?: string;
        '24x24'?: string;
        '32x32'?: string;
        '48x48'?: string;
      };
    };
    description?: any;
    created?: string;
    updated?: string;
    duedate?: string;
    priority?: {
      name: string;
      iconUrl?: string;
    };
    issuetype?: {
      name: string;
      iconUrl?: string;
    };
    reporter?: {
      displayName: string;
    };
  };
}

export interface JiraIssuesResponse {
  success: boolean;
  count: number;
  total: number;
  issues: JiraIssue[];
  error?: string;
}

export interface JiraSearchResponse {
  success: boolean;
  count: number;
  total: number;
  issues: JiraIssue[];
  error?: string;
}

export interface JiraBoard {
  id: number;
  name: string;
  type: string;
  location?: {
    projectId: number;
    projectKey: string;
    projectName: string;
  };
}

export interface JiraBoardsResponse {
  success: boolean;
  count: number;
  boards: JiraBoard[];
  error?: string;
}

export interface JiraProjectDetails {
  id: string;
  key: string;
  name: string;
  description?: string;
  lead?: {
    displayName: string;
  };
  url?: string;
  projectTypeKey: string;
  avatarUrls?: {
    '48x48'?: string;
  };
}

export interface JiraProjectDetailsResponse {
  success: boolean;
  project: JiraProjectDetails;
  error?: string;
}

export const jiraApi = {
  getProjects: () => apiClient.get<JiraProjectsResponse>('/api/jira/projects'),
  getProject: (projectKeyOrId: string) => apiClient.get<JiraProjectDetailsResponse>(`/api/jira/project/${projectKeyOrId}`),
  getBoards: (projectKeyOrId?: string) => apiClient.get<JiraBoardsResponse>('/api/jira/boards', { params: { projectKeyOrId } }),
  getBoardConfiguration: (boardId: number) => apiClient.get(`/api/jira/board/${boardId}/configuration`),
  getBoardIssues: (boardId: number, jql?: string) => apiClient.get<JiraIssuesResponse>(`/api/jira/board/${boardId}/issues`, { params: { jql } }),
  getIssue: (issueKey: string) => apiClient.get<JiraIssue>(`/api/jira/issue/${issueKey}`),
  searchIssues: (jql: string, fields?: string[]) =>
    apiClient.post<JiraSearchResponse>('/api/jira/search', { jql, fields }),
  createIssue: (data: { projectKey: string; summary: string; description: string; issueType?: string }) =>
    apiClient.post('/api/jira/issue', data),
  createProject: (data: { key: string; name: string; description?: string; projectTypeKey?: string }) =>
    apiClient.post('/api/jira/project', data),
  addComment: (issueKey: string, comment: string) =>
    apiClient.post(`/api/jira/issue/${issueKey}/comment`, { comment }),
  getTransitions: (issueKey: string) => apiClient.get(`/api/jira/issue/${issueKey}/transitions`),
};

// Confluence API
export interface ConfluenceSpace {
  id: string;
  key: string;
  name: string;
  type: string;
  _links?: {
    webui?: string;
  };
}

export interface ConfluenceSpacesResponse {
  success: boolean;
  count: number;
  spaces: ConfluenceSpace[];
  sandbox?: boolean;
  error?: string;
  hint?: string;
}

export interface ConfluencePage {
  id: string;
  title: string;
  type: string;
  space?: {
    key: string;
    name: string;
  };
  version?: {
    number: number;
  };
  body?: {
    storage?: {
      value: string;
      representation: string;
    };
    view?: {
      value: string;
      representation: string;
    };
    export_view?: {
      value: string;
      representation: string;
    };
  };
  history?: {
    createdBy?: {
      displayName: string;
      userKey: string;
    };
    createdDate: string;
    lastUpdated?: {
      by?: {
        displayName: string;
        userKey: string;
      };
      when: string;
    };
  };
  _links?: {
    webui?: string;
  };
}

export interface ConfluenceComment {
  id: string;
  title: string;
  body?: {
    view?: {
      value: string;
      representation: string;
    };
  };
  history?: {
    createdBy?: {
      displayName: string;
      userKey: string;
    };
    createdDate: string;
  };
}

export interface ConfluencePagesResponse {
  success: boolean;
  count: number;
  pages: ConfluencePage[];
  error?: string;
}

export interface ConfluenceSearchResponse {
  success: boolean;
  count: number;
  results: ConfluencePage[];
  error?: string;
}

export interface ConfluenceCommentsResponse {
  success: boolean;
  count: number;
  comments: ConfluenceComment[];
  error?: string;
}

export interface ConfluencePageResponse {
  success: boolean;
  page: ConfluencePage;
  error?: string;
}

export const confluenceApi = {
  getSpaces: () => apiClient.get<ConfluenceSpacesResponse>('/api/confluence/spaces'),
  getSpace: (spaceKey: string) => apiClient.get(`/api/confluence/space/${spaceKey}`),
  getPages: (spaceKey: string, limit?: number) =>
    apiClient.get<ConfluencePagesResponse>('/api/confluence/pages', {
      params: { spaceKey, limit },
    }),
  getPageById: (pageId: string) => apiClient.get<ConfluencePageResponse>(`/api/confluence/page/${pageId}`),
  createPage: (data: { spaceKey: string; title: string; content: string; parentId?: string }) =>
    apiClient.post('/api/confluence/page', data),
  searchContent: (cql: string, limit?: number) =>
    apiClient.get<ConfluenceSearchResponse>('/api/confluence/search', {
      params: { cql, limit },
    }),
  getPageComments: (pageId: string) => apiClient.get<ConfluenceCommentsResponse>(`/api/confluence/page/${pageId}/comments`),
  addComment: (pageId: string, comment: string) =>
    apiClient.post(`/api/confluence/page/${pageId}/comments`, { comment }),
};

// Health check
export const healthCheck = () => apiClient.get('/health');

export default apiClient;
